// Promo modals: which of the modals this page offers a visitor sees, and when. The server
// renders every candidate hidden, in queue order (app/View/Components/PromoModals.php); this
// file applies the per-visitor rules. See docs/promo-modals.md sections 5 to 8.
//
//   - After a short delay, open the first candidate the visitor has not hit the limit for.
//     When it closes, wait a moment and open the next, until none are left.
//   - Never open over another modal: wait for it to close.
//   - "Once a day" and the rest are counted from when a modal was SHOWN, against its image
//     version, so a new image shows it to everyone again.
//   - A modal can skip the first page of a visit that arrived from a search engine.
//   - The homepage spa swap brings new candidates; a modal already shown on this document is
//     not shown again, so swapping spa does not repeat one.
//
// There is no JS test runner in this project, so docs/promo-modals.md lists the journeys to
// check by hand.

import { onContentReady, onContentReplacing } from './shared/contentLifecycle.js';

const MODAL_SELECTOR = '#modals_container aside.promo_modal[data-promo-modal]';

// Before the first modal of a page. Long enough that the page is seen first, which also helps
// with Google's rule on pop-ups covering content (docs/promo-modals.md section 7).
const DELAY_MS = 2000;

// Between one modal closing and the next opening, so the second reads as a new modal rather
// than the first flickering.
const GAP_MS = 400;

// The same query as PromoModals::MOBILE_MEDIA, to report which image was shown.
const MOBILE_MEDIA = '(max-width: 767.98px)';

// When each modal was last shown, by id: { "12": { "v": 2, "at": 1760000000000 } }.
const SEEN_KEY = 'promo_modals_seen';

// "Once per visit": a cookie with no expiry, so the browser drops it when its session ends,
// holding "id:version" pairs. Written and read here only; PHP never reads it, so it needs no
// place in bootstrap/app.php's encryptCookies except list (docs/promo-modals.md section 6).
const SESSION_COOKIE = 'promo_modals_session';

const WINDOWS_MS = {
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
};

// Hosts a visit from a search engine arrives from. Google Ads clicks come through Google too,
// so paid visitors are skipped as well - accepted (docs/promo-modals.md section 7).
const SEARCH_HOSTS = [
    /(^|\.)google\.[a-z.]+$/,
    /(^|\.)googleadservices\.com$/,
    /(^|\.)bing\.com$/,
    /(^|\.)duckduckgo\.com$/,
    /(^|\.)yahoo\.[a-z.]+$/,
    /(^|\.)ecosia\.org$/,
    /(^|\.)search\.brave\.com$/,
];

// Ids shown since this document loaded. A swap keeps it, a real navigation starts afresh.
const shownThisDocument = new Set();

// Whether this document's own content has been looked at yet. Only the first pass can be a
// search arrival: a swap keeps the document, so document.referrer still names Google after it.
let firstPass = true;

// Bumped by every pass, so a pending timer from the content a swap replaced does nothing.
let generation = 0;

onContentReady((root) => {
    // One pass per page: the initial document, then the modals region of each swap.
    if (root !== document && root.id !== 'modals_container') {
        return;
    }

    begin();
});

// Release the scroll lock before a swap removes an open promo modal, or it would stay locked
// with nothing left to close. Back or forward can swap the content while one is open.
onContentReplacing((root) => {
    if (root.id === 'modals_container' && root.querySelector('aside.promo_modal.visible')) {
        window.hideModal();
    }
});

function begin() {
    const pass = ++generation;
    const searchArrival = firstPass && cameFromSearch();

    firstPass = false;

    const modals = [...document.querySelectorAll(MODAL_SELECTOR)];

    const preview = modals.find((modal) => 'promoModalPreview' in modal.dataset);

    // A preview opens at once, ignoring the frequency, search arrivals and anything shown
    // before - but on the next tick, never synchronously. This file is imported by app.js
    // BEFORE app.js defines window.showModal(), and onContentReady() calls back immediately
    // while a module script runs (readyState is already 'interactive'), so opening here and
    // now threw "window.showModal is not a function" and showed nothing.
    if (preview) {
        setTimeout(() => {
            if (pass === generation && preview.isConnected) {
                open(preview, { preview: true });
            }
        }, 0);

        return;
    }

    const queue = modals.filter((modal) => {
        if (shownThisDocument.has(modal.dataset.promoModal)) {
            return false;
        }

        if (searchArrival && 'promoModalSkipSearch' in modal.dataset) {
            return false;
        }

        return ! limitReached(modal);
    });

    if (queue.length) {
        setTimeout(() => showNext(pass, queue), DELAY_MS);
    }
}

function showNext(pass, queue) {
    if (pass !== generation || ! queue.length) {
        return;
    }

    const modal = queue[0];

    if (! modal.isConnected) {
        return;
    }

    // Something else is open - a 12 Weeks of Christmas box, the dynamic modal. Wait for it.
    const other = document.querySelector('#modals_container .modal.visible');

    if (other) {
        other.addEventListener('hide', () => setTimeout(() => showNext(pass, queue), GAP_MS), { once: true });

        return;
    }

    open(modal);

    modal.addEventListener('hide', () => setTimeout(() => showNext(pass, queue.slice(1)), GAP_MS), { once: true });
}

function open(modal, { preview = false } = {}) {
    const body = modal.querySelector('.promo_modal_body');
    const template = modal.querySelector('template.promo_modal_template');

    if (body && template && ! body.hasChildNodes()) {
        body.replaceChildren(template.content.cloneNode(true));
    }

    const opener = document.activeElement;

    window.showModal(modal.dataset.modalId);

    const close = modal.querySelector('.promo_modal_close');

    close?.focus({ preventScroll: true });

    const onKeydown = (event) => trapFocus(event, modal);
    const onCloseClick = (event) => {
        // A keyboard press arrives as a click with no pointer (detail 0). The site's own close
        // handling listens for mousedown only, so without this Enter on the focused close
        // button would do nothing. A mouse click has already closed it on mousedown.
        if (event.detail === 0) {
            window.hideModal();
        }
    };

    modal.addEventListener('keydown', onKeydown);
    close?.addEventListener('click', onCloseClick);

    modal.addEventListener('hide', () => {
        modal.removeEventListener('keydown', onKeydown);
        close?.removeEventListener('click', onCloseClick);

        // Back to wherever the visitor was, if that is still on the page.
        if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected) {
            opener.focus({ preventScroll: true });
        }
    }, { once: true });

    if (preview) {
        return;
    }

    shownThisDocument.add(modal.dataset.promoModal);
    remember(modal);
    track('view_promotion', modal);
    count('view', modal);
}

// Clicks on a modal's link, delegated so swapped-in modals need nothing.
document.addEventListener('click', (event) => {
    const link = event.target.closest('.promo_modal_link');
    const modal = link?.closest(MODAL_SELECTOR);

    if (modal && ! ('promoModalPreview' in modal.dataset)) {
        track('select_promotion', modal);
        count('click', modal);
    }
});

// Keep Tab inside the open modal: its close button and, if it has one, its link.
function trapFocus(event, modal) {
    if (event.key !== 'Tab') {
        return;
    }

    const focusable = [...modal.querySelectorAll('button, a[href]')];

    if (! focusable.length) {
        return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (! event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}

function limitReached(modal) {
    const id = modal.dataset.promoModal;
    const version = Number(modal.dataset.promoModalVersion);
    const frequency = modal.dataset.promoModalFrequency;

    if (frequency === 'every_load') {
        return false;
    }

    if (frequency === 'session') {
        return sessionSeen().has(`${id}:${version}`);
    }

    const entry = readSeen()[id];

    if (! entry || entry.v !== version) {
        return false;
    }

    if (frequency in WINDOWS_MS) {
        return Date.now() - entry.at < WINDOWS_MS[frequency];
    }

    // 'once', and anything this file does not know yet, which errs towards not repeating.
    return true;
}

function remember(modal) {
    const id = modal.dataset.promoModal;
    const version = Number(modal.dataset.promoModalVersion);

    // Storage can be unavailable (private browsing, blocked site data). The worst case is a
    // modal seen again sooner than its limit, never an error.
    try {
        const seen = readSeen();

        seen[id] = { v: version, at: Date.now() };
        localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    } catch {
        // See above.
    }

    const pairs = sessionSeen();

    pairs.add(`${id}:${version}`);

    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${SESSION_COOKIE}=${encodeURIComponent([...pairs].join(','))}; path=/; SameSite=Lax${secure}`;
}

function readSeen() {
    try {
        const seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}');

        return seen && typeof seen === 'object' && ! Array.isArray(seen) ? seen : {};
    } catch {
        return {};
    }
}

function sessionSeen() {
    const match = document.cookie.split('; ').find((part) => part.startsWith(`${SESSION_COOKIE}=`));

    if (! match) {
        return new Set();
    }

    return new Set(decodeURIComponent(match.slice(SESSION_COOKIE.length + 1)).split(',').filter(Boolean));
}

function cameFromSearch() {
    try {
        const host = new URL(document.referrer).hostname;

        return SEARCH_HOSTS.some((pattern) => pattern.test(host));
    } catch {
        // No referrer, or not a URL: a direct visit.
        return false;
    }
}

// Our own view and click counts (PromoModalEventController). keepalive so a click still
// counts while the link is navigating away. Fire and forget: a failure, or a 429 from the
// route's throttle, only means one uncounted event, so nothing is reported or retried.
function count(event, modal) {
    const url = modal.dataset.promoModalEventUrl;

    if (! url) {
        return;
    }

    try {
        fetch(url, {
            method: 'POST',
            keepalive: true,
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': window.csrfToken(),
            },
            body: JSON.stringify({ event }),
        }).catch(() => {});
    } catch {
        // No CSRF meta tag, or fetch unavailable: the event goes uncounted.
    }
}

// GA4's own promotion events, through Google Tag Manager. Without a dataLayer on the page this
// does nothing. No personal data (docs/promo-modals.md section 8).
function track(event, modal) {
    if (! Array.isArray(window.dataLayer)) {
        return;
    }

    const mobile = 'promoModalHasMobile' in modal.dataset && window.matchMedia(MOBILE_MEDIA).matches;

    // GA4's guidance: clear the previous ecommerce object first, or its fields leak into this one.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event,
        ecommerce: {
            promotion_id: modal.dataset.promoModal,
            promotion_name: modal.dataset.promoModalName,
            creative_name: mobile ? 'mobile' : 'desktop',
        },
    });
}
